import { type FC, type RefObject, useEffect, useRef } from "react";
import { useResizeObserver } from "widgets/hooks";
import { useShallow } from "zustand/shallow";

import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../../main-canvas/render-main.ts";
import { disableUserEventListeners, setupUserEventListeners } from "../../main-canvas/user-events/user-events.ts";
import { TrackEnum } from "../../openapi";
import { useGenomeViewerStore } from "../../store/store.ts";
import { LoadingDNA } from "./loading-dna.tsx";
import { InfoPopover } from "./tooltip/InfoPopover.tsx";
import { RegionInfoPopover } from "./tooltip/RegionInfoPopover.tsx";

export const Canvas: FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const setReady = useGenomeViewerStore((state) => state.setReady);
  const hoveringRangeSelectionIndicator = useGenomeViewerStore(
    (state) => state.leftSelectionHovering || state.rightSelectionHovering,
  );
  const selectedTrackType = useGenomeViewerStore(
    useShallow((state) => state.tracks.get(state.contextualMenuData?.trackId ?? "")?.trackType ?? undefined),
  );

  useEffect(() => {
    const canvas = ref.current;

    if (canvas) {
      setReady(canvas);
      setupUserEventListeners();
    }

    return () => {
      disableUserEventListeners();
    };
  }, [setReady]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        flex: "1 1 auto",
        overflow: "hidden",
        position: "relative",
        cursor: hoveringRangeSelectionIndicator ? "e-resize" : "default",
      }}
    >
      <canvas
        ref={ref}
        data-testid="main-canvas"
        id="genome-viewer-canvas"
        onPointerEnter={() => {
          useGenomeViewerStore.setState({
            disableKeyboardShortcuts: false,
          });
          refreshMain();
        }}
        onPointerLeave={() => {
          useGenomeViewerStore.setState({
            hoverData: undefined,
            hoveredGroupId: undefined,
            hoveredTackId: undefined,
            disableKeyboardShortcuts: true,
          });
          refreshMain();
        }}
      />
      <LoadingDNA />
      <ResizeContainer containerRef={containerRef} />
      {selectedTrackType && selectedTrackType === TrackEnum.SEQUENCE ? <RegionInfoPopover /> : <InfoPopover />}
    </div>
  );
};

interface ResizeContainerProps {
  containerRef: RefObject<HTMLElement>;
}

const ResizeContainer: FC<ResizeContainerProps> = (props) => {
  const rect = useResizeObserver(props.containerRef);
  const canvas = useGenomeViewerStore((state) => state.canvas);

  useEffect(() => {
    if (canvas && rect?.width && rect.height) {
      const ratio = 1; //window.devicePixelRatio;

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;

      refreshTracks();
      refreshMain();
    }
  }, [rect?.width, rect?.height, canvas]);

  return null;
};
